import React, { useEffect, useState } from 'react';
import { IWidgetWrapper, WorkspaceProps } from './types';

import './widgetsGrid.css';
import { ActionButton, Loading, Icon, TagList } from '../UI';
import { WidgetLibrary } from './WidgetLibrary/WidgetLibrary';
import { DropdownMenu } from '../form';
import WorkspaceManager from '../WorkspaceManager/WorkspaceManager';
import { useUserStrategies } from '../../context/UserStrategies';
import { useAuth } from '../../context/Auth';
import { useWorkspaces } from '../../context/Workspaces';
import { useTranslation } from 'react-i18next';
import { SimpleDropdownItem } from '../form/Dropdown-menu/SimpleDropdownItem';
import { useMutation } from '@apollo/client';
import { UPDATE_STRATEGY } from '../../http/apollo-queries';
import { toast } from 'react-toastify';
import { Modal } from '../Modal';
import { GetStrategyData } from '../Strategies/GetStrategyData';
import { AdvancedSettings } from '../Strategies/AdvancedSettings';
import { useStrategiesContext } from '../Strategies/StrategiesContext';
import { logger } from '../../helpers/logger';
import { IWorkspaceWidget } from '../../types/model';
import { WidgetsGrid } from './WidgetsGrid';
import { WidgetWrapper } from './WidgetWrapper';
import { CurrencyCodeEnum } from '../../constants/enums';

export const Workspace = ({
  allowAddWidget = true,
  category,
  draggableActive = true,
  fixed,
  pageTitle,
  resizableActive = true,
  searchTerm,
  strategyId,
  workspaceId,
}: WorkspaceProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const {
    lastFetchDate,
    strategies,
    refetch: refetchStrategies,
  } = useUserStrategies();

  const {
    workspace,
    setStrategyWorkspace,
    setWorkspaceWidgetsPositions,
    setWorkspaceWidgetSize,
    removeWidgetFromWorkspace,
    refetchWorkspace,
    widgetLoading,
    workspaceLoading,
  } = useWorkspaces();

  const { showAddWidgetModal, handleAddWidgetModal } = useStrategiesContext();

  const strategy = strategies.find(({ id }) => id === strategyId);

  const {
    currency: strategyCurrency,
    color,
    endDate,
    id,
    name,
    notifyEnd,
    startDate,
  } = strategy ?? {
    currency: CurrencyCodeEnum.USD,
    color: '',
    endDate: '',
    id: '',
    name: '',
    notifyEnd: true,
    startDate: '',
  };

  const [updateStrategy] = useMutation(UPDATE_STRATEGY);

  const gridColsPerBreakpoint = { xl: 5, lg: 4, md: 3, sm: 2, xs: 1 };

  const [workspaceWidgets, setWorkspaceWidgets] = useState<IWorkspaceWidget[]>(
    []
  );

  useEffect(() => {
    if (workspace?.id) {
      logger.log('Workspace', workspace);

      // Create WorkspaceWidget List with only valid items/category and force sorting by position
      const sortedWidgetList = [
        ...workspace.workspaceWidgets.items.filter((workspaceWidget) => {
          return category
            ? workspaceWidget?.widget?.category === category
            : workspaceWidget?.id;
        }),
      ].filter(
        ({
          widget: {
            membershipPlans: { items: widgetMembershipPlans = [] } = {},
          } = {},
        }) =>
          widgetMembershipPlans.some(
            (widgetMembershipPlan) =>
              widgetMembershipPlan?.membershipPlan?.id ===
              user?.membership?.membershipPlan?.id
          )
      );
      sortedWidgetList.sort((a, b) => a.position - b.position);
      setWorkspaceWidgets(sortedWidgetList);
    }
  }, [workspace]);

  const [isWorkspaceMenuPopupShown, setIsWorkspaceMenuPopupShown] =
    useState<boolean>(false);
  const [workspaceMenuInitState, setWorkspaceMenuInitState] = useState<
    'manage' | 'save'
  >('manage');

  const [selectedWorkspaceWidgetId, setSelectedWorkspaceWidgetId] =
    useState<string>('');

  const [isAddWidgetFullscreenPopupShown, setIsAddWidgetFullscreenPopupShown] =
    useState<boolean>(false);

  const handleWidgetInstall = (
    id: string,
    position: number,
    size: string,
    title: string,
    componentName: string
  ) => {
    logger.log('Workspace:handleWidgetInstall', {
      id,
      title,
      position,
      size,
      componentName,
    });

    refetchWorkspace();
  };

  const handleWidgetRemove = (id: string) => {
    logger.log('Workspace:handleWidgetRemove', {
      id,
    });
    refetchWorkspace();
  };

  const handleWidgetCloseClick = (id: string) => {
    logger.log('Workspace:handleWidgetCloseClick', id);

    setSelectedWorkspaceWidgetId(id);
    removeWidgetFromWorkspace({
      variables: {
        workspaceId,
        workspaceWidgetId: id,
      },
    })
      .then(
        ({
          data: { removeWidgetFromWorkspace: { status = '' } = {} } = {},
        }) => {
          logger.log('Workspace:removeWidgetFromWorkspace', status);

          if (status === 'SUCCESS') {
            setWorkspaceWidgets(workspaceWidgets.filter((w) => w.id !== id));
          }
        }
      )
      .then(() => refetchWorkspace())
      .catch((err) => {
        console.error(err);
      });
  };

  const handleItemResize = (id: string, w: number, h: number) => {
    logger.log('Workspace:handleResize', id, w, h);

    setWorkspaceWidgetSize({
      variables: {
        workspaceWidgetId: id,
        size: `COL${w}H${h}`,
      },
    })
      .then(() => refetchWorkspace())
      .catch((err) => {
        console.error(err);
      });
  };

  const handleItemMove = (id: string, position: number, ids: string[]) => {
    logger.log('WidgetGrid:handleItemMove', id, position);

    setWorkspaceWidgetsPositions({
      variables: {
        workspaceWidgetIds: ids,
        workspaceId,
      },
    }).catch((err) => {
      console.error(err);
    });
  };

  const handleItemFocus = (id: string) => {
    setSelectedWorkspaceWidgetId(id);
  };
  const handleChangeCurrency = (currency: string) => {
    updateStrategy({
      variables: {
        input: {
          id,
          name,
          currency,
          color,
          startDate,
          endDate,
          notifyEnd,
        },
      },
    })
      .then(() => refetchStrategies())
      .then(() => toast.success(t('updateStrategy.currency.success')));
  };

  const [modalElement, setModalElement] = useState<JSX.Element>();

  return (
    <>
      <Modal
        element={modalElement || <></>}
        handleMerlinLogo={true}
        isOpen={typeof modalElement !== 'undefined'}
        onCloseClick={() => setModalElement(undefined)}
        height={'auto'}
      />
      {workspaceLoading ? (
        <Loading status={true} className={'mx-auto'} />
      ) : (
        <div
          className={`flex flex-col bg-white w-full dark:bg-black-real dark:text-white`}
        >
          <header className="flex gap-5 justify-between">
            {pageTitle && (
              <div className="flex md:w-full-11.5 items-center justify-between w-full h-15 bg-white border border-grey-30 rounded-xl shadow-md dark:bg-grey-90 dark:border-grey-70">
                {category === 'TRACKING' ? (
                  <>
                    <div className={'flex flex-row w-11/12 md:w-7/12'}>
                      <h1 className="mx-4 font-semibold text-2xl whitespace-nowrap overflow-ellipsis overflow-hidden">
                        {pageTitle}
                      </h1>
                      <div className={'hidden lg:flex'}>
                        <TagList
                          tagList={strategy?.tags}
                          isReadOnly={true}
                          maxTags={3}
                          className={'flex-nowrap'}
                        />
                      </div>
                    </div>
                    <div
                      className={
                        'flex flex-row md:w-5/12 items-center justify-end'
                      }
                    >
                      <div className={'text-sm ml-6 hidden lg:block'}>
                        <span className={'text-grey-30 mr-2'}>
                          {t('widgetsGrid.timeframe')}:
                        </span>
                        <span>
                          {strategy?.startDate} / {strategy?.endDate}
                        </span>
                      </div>
                      <div className={'text-sm ml-6 hidden md:block'}>
                        <span className={'text-grey-30 mr-2'}>
                          {t('widgetsGrid.reportingCurrency')}:
                        </span>
                        <span>{strategy?.currency}</span>
                      </div>

                      <div className="relative flex items-center justify-center mx-4 h-full">
                        <DropdownMenu
                          dropdownTooltipClassName="bg-white border-solid border-grey-10 border shadow-md"
                          dropdownTooltipXOffset={-75}
                          dropdownItems={[
                            {
                              item: t('widgetsGrid.reportingCurrency'),
                              dropdownItems: Object.values(
                                CurrencyCodeEnum
                              ).map((currency) => (
                                <SimpleDropdownItem
                                  key={currency}
                                  selected={strategyCurrency}
                                  title={currency}
                                  onClick={handleChangeCurrency}
                                />
                              )),
                            },
                            {
                              item: t('widgetsGrid.workspace'),
                              dropdownItems: [
                                // ...workspaces.map((w, index, arr) => {
                                //   return {
                                //     item: w.name,
                                //     withBorder: index === arr.length - 1,
                                //     key: 'set_workspace',
                                //   };
                                // }),
                                {
                                  item: t('widgetsGrid.saveWorkspace'),
                                  key: 'save_workspace',
                                },
                                {
                                  item: t('widgetsGrid.manageWorkspaces'),
                                  key: 'manage_workspaces',
                                },
                              ],
                              dropdownItemsData: [], //workspaces,
                            },
                            {
                              item: t('widgetsGrid.advancedSettings'),
                              key: 'advanced_settings',
                            },
                            {
                              item: t('widgetsGrid.export'),
                              key: 'export_strategy',
                            },
                          ]}
                          handleClick={(item, data, key) => {
                            logger.log('WidgetGrid:click -> ', item, data, key);
                            if (data?.id) {
                              setStrategyWorkspace({
                                variables: {
                                  targetWorkspaceId: data.id,
                                  currentWorkspaceId: workspaceId,
                                  strategyId,
                                  profileId: user!.profile!.id,
                                },
                              }).then(() => refetchWorkspace());
                            } else if (key === 'manage_workspaces') {
                              setIsWorkspaceMenuPopupShown(true);
                              setWorkspaceMenuInitState('manage');
                            } else if (key === 'save_workspace') {
                              setIsWorkspaceMenuPopupShown(true);
                              setWorkspaceMenuInitState('save');
                            } else if (key === 'export') {
                              setModalElement(
                                <GetStrategyData
                                  strategyId={strategyId}
                                  close={() => setModalElement(undefined)}
                                />
                              );
                            } else if (key === 'advanced_settings') {
                              setModalElement(
                                <AdvancedSettings
                                  strategyId={strategyId}
                                  close={() => setModalElement(undefined)}
                                />
                              );
                            }
                          }}
                          hideOnItemClick
                        >
                          <Icon className="cursor-pointer" svgName="options" />
                        </DropdownMenu>
                      </div>
                    </div>
                  </>
                ) : (
                  <h2 className="ml-4 font-semibold text-xl">{pageTitle}</h2>
                )}
              </div>
            )}
            {allowAddWidget && (
              <div
                className={`
                    flex
                    flex-shrink-0
                    items-center
                    justify-center
                    w-16
                    md:w-40
                    h-15
                    border
                    border-grey-30
                    dark:border-grey-70
                    dark:bg-grey-90
                    rounded-xl
                    shadow-md
                  `}
              >
                <ActionButton
                  text="Add widget"
                  size="large"
                  iconPosition={'right'}
                  hideTextOnMobile={true}
                  highlightIcon={true}
                  onClick={() =>
                    handleAddWidgetModal?.() ??
                    setIsAddWidgetFullscreenPopupShown(true)
                  }
                />
              </div>
            )}
          </header>

          <div
            className={`
              -mx-4
              w-full
            `}
          >
            {isWorkspaceMenuPopupShown && (
              <WorkspaceManager
                initState={workspaceMenuInitState}
                workspaceId={workspaceId}
                onCloseClick={() => setIsWorkspaceMenuPopupShown(false)}
              />
            )}
            <WidgetsGrid
              gridColsPerBreakpoint={gridColsPerBreakpoint}
              resizableActive={resizableActive}
              onItemResize={handleItemResize}
              onItemMove={handleItemMove}
              onItemFocus={handleItemFocus}
              widgets={workspaceWidgets.map(
                (workspaceWidget): IWidgetWrapper => ({
                  id: workspaceWidget.id,
                  width: +workspaceWidget.size.charAt(3),
                  height: +workspaceWidget.size.charAt(5),
                  locked:
                    workspaceWidget?.locked ??
                    workspaceWidget.widget?.locked ??
                    true,
                  children: (
                    <WidgetWrapper
                      id={workspaceWidget.id}
                      key={workspaceWidget.id}
                      position={+workspaceWidget.position}
                      w={+workspaceWidget.size.charAt(3)}
                      h={+workspaceWidget.size.charAt(5)}
                      componentName={workspaceWidget!.widget!.componentName}
                      selected={selectedWorkspaceWidgetId === id}
                      closeActive={!fixed && !workspaceWidget.locked}
                      searchTerm={searchTerm}
                      title={workspaceWidget?.widget?.name || ''}
                      widgetDescription={
                        workspaceWidget?.widget?.description || ''
                      }
                      draggableActive={draggableActive}
                      loading={widgetLoading}
                      infoActive={true}
                      snapshotActive={true}
                      handleWidgetCloseClick={handleWidgetCloseClick}
                      strategyId={strategyId}
                      lastUpdateDate={lastFetchDate}
                    />
                  ),
                })
              )}
            />

            {(showAddWidgetModal || isAddWidgetFullscreenPopupShown) && (
              <WidgetLibrary
                category={category}
                onWidgetInstall={handleWidgetInstall}
                onCloseClick={() =>
                  handleAddWidgetModal?.() ??
                  setIsAddWidgetFullscreenPopupShown(false)
                }
                onWidgetRemove={handleWidgetRemove}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
